import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, of, Subject, switchMap, throwError } from 'rxjs';
import {
  UserAPIModelsERoles,
  UserAPIModelsUser,
  UserService as UserClient,
} from 'user-ts-ng-client';

import { normalizeUserRoles } from '@shared/utils/user-role.utils';
import { AuthService } from '@core/auth/auth.service';
import { USERS_MOCK } from '@mocks/users.mock';

import { ActionType } from '../models/organization.model';
import { User, UserFormValue } from '../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _isLoadingUsers$ = new BehaviorSubject<boolean>(false);
  private _shouldShowUserActions$ = new BehaviorSubject<boolean>(false);
  private _triggerUserFormAction$ = new Subject<ActionType>();
  private userClient = inject(UserClient);
  private authService = inject(AuthService);
  private userSource = new BehaviorSubject<User | null>(null);
  private userUpdatedSource = new Subject<void>();
  private _actionType$ = new BehaviorSubject<ActionType | undefined>(undefined);

  public user$ = this.userSource.asObservable();
  public userUpdated$ = this.userUpdatedSource.asObservable();
  public actionType$ = this._actionType$.asObservable();

  public get isLoadingUsers$(): Observable<boolean> {
    return this._isLoadingUsers$.asObservable();
  }
  public get shouldShowUserActions$(): Observable<boolean> {
    return this._shouldShowUserActions$.asObservable();
  }

  public get triggerFormUserAction(): Observable<ActionType> {
    return this._triggerUserFormAction$.asObservable();
  }

  public setActionType(actionType: ActionType): void {
    this._actionType$.next(actionType);
  }

  public setUser(user: User): void {
    this.userSource.next(user);
  }

  public notifyUserUpdated(): void {
    this.userUpdatedSource.next();
  }

  public triggerAction(action: ActionType): void {
    this._triggerUserFormAction$.next(action);
  }

  public getUsers(): Observable<User[]> {
    return of(USERS_MOCK);
  }

  public getUsersByOrganizationId(orgId: string): Observable<User[]> {
    return this.userClient.v1UserGetByOrganizationOrganizationIdGet(orgId) as unknown as Observable<
      User[]
    >;
  }

  public getUser(id: string): Observable<User> {
    return this.userClient.v1UserUserIdGet(id) as unknown as Observable<User>;
  }

  public getUserByEmail(email: string): Observable<User> {
    return this.userClient.v1UserGet(email) as unknown as Observable<User>;
  }

  public impersonateUser(impersonatingAsId: string): Observable<User> {
    return this.updateCurrentUserImpersonationState(impersonatingAsId);
  }

  public stopImpersonation(): Observable<User> {
    return this.updateCurrentUserImpersonationState(null);
  }

  public getAllowedRoleAssignments(): Observable<readonly UserAPIModelsERoles[]> {
    return this.userClient
      .v1UserAllowedRoleAssignmentsGet()
      .pipe(map((roles: Array<string> | null | undefined) => normalizeUserRoles(roles)));
  }

  public showUserMenuActions(): void {
    this._shouldShowUserActions$.next(true);
  }
  public hideUserMenuActions(): void {
    this._shouldShowUserActions$.next(false);
  }

  public createUser(
    user: UserFormValue,
    organizationId: string,
  ): Observable<{ success: boolean; userName: string }> {
    return this.userClient
      .v1UserPost({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        organizationId: organizationId,
        phone: user.phone,
        role: user.role,
      })
      .pipe(
        map((createdUser: UserAPIModelsUser) => ({
          success: !!createdUser,
          userName: createdUser?.name ?? '',
        })),
      );
  }

  public updateUser(
    formValue: UserFormValue,
    existingUser: User,
  ): Observable<{ success: boolean; userName: string }> {
    const updatedUser: UserAPIModelsUser = {
      id: existingUser.id,
      organizationId: existingUser.organizationId,
      name: [formValue.firstName, formValue.middleName, formValue.lastName]
        .filter(Boolean)
        .join(' '),
      email: formValue.email,
      phone: formValue.phone,
      role: formValue.role ?? existingUser.role,
      invitationToken: existingUser.invitationToken,
      customClaims: existingUser.customClaims,
    };
    return this.userClient.v1UserPut(updatedUser).pipe(
      map((result: UserAPIModelsUser) => ({
        success: !!result,
        userName: result?.name ?? '',
      })),
    );
  }

  public deleteUser(userId: string): Observable<{ success: boolean }> {
    return this.userClient.v1UserDelete(userId).pipe(map(() => ({ success: true })));
  }

  public sendPasswordResetLink(userId: string): Observable<void> {
    return this.userClient.v1UserUserIdPasswordResetPost(userId).pipe(map(() => undefined));
  }

  private updateCurrentUserImpersonationState(impersonatingAsId: string | null): Observable<User> {
    const currentUserEmail = this.authService.getActiveAccount()?.username;

    if (!currentUserEmail) {
      return throwError(() => new Error('Current session email is unavailable'));
    }

    return this.getUserByEmail(currentUserEmail).pipe(
      switchMap((currentUserSummary) => {
        if (!currentUserSummary.id) {
          return throwError(() => new Error('Current user id is unavailable'));
        }

        return this.getUser(currentUserSummary.id);
      }),
      switchMap((currentUser) => {
        const { canImpersonate: _deprecatedCanImpersonate, ...payloadWithoutDeprecatedFlag } =
          currentUser as UserAPIModelsUser & { canImpersonate?: boolean | null };
        const payload = {
          ...payloadWithoutDeprecatedFlag,
          impersonatingAsId,
        } as UserAPIModelsUser;

        return this.userClient.v1UserPut(payload).pipe(map(() => currentUser));
      }),
      switchMap((currentUser) => of(currentUser)),
    );
  }
}
